import axios from "axios";
import { getSession } from "next-auth/react";

const API_URL = "https://api.dev.yolk.coach/_internal/app";
export const clientFetcher = axios.create({
  baseURL: process.env.NEXT_PUBLIC_YOLK_API_URL || API_URL,
});

// Supplies the workspace the user is currently acting in. When set, every
// request carries X-Workspace-Id so user-scoped backend endpoints don't have
// to infer the workspace. Wired up by WorkspaceStore.
let workspaceIdProvider: (() => string | undefined) | undefined;

export const setWorkspaceIdProvider = (
  provider: () => string | undefined,
): void => {
  workspaceIdProvider = provider;
};

// Called by WorkspaceStore when a 410 with a stale X-Workspace-Id is detected.
// Recovery is best-effort and one-shot per id — the original rejection always
// propagates so the caller sees the error while the store silently refreshes.
let staleWorkspaceHandler: ((staleId: string) => void) | undefined;

export const setStaleWorkspaceHandler = (
  handler: (staleId: string) => void,
): void => {
  staleWorkspaceHandler = handler;
};

clientFetcher.interceptors.request.use(async (config) => {
  const session = await getSession();

  if (session?.accessToken) {
    config.headers.set("Authorization", `Bearer ${session.accessToken}`);
  }

  const workspaceId = workspaceIdProvider?.();
  if (workspaceId && !config.headers.has("X-Workspace-Id")) {
    config.headers.set("X-Workspace-Id", workspaceId);
  }

  if (!(config.data instanceof FormData)) {
    config.headers.set("Content-Type", "application/json");
  }
  config.headers.set("Accept", "application/json");

  return config;
});

clientFetcher.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (
      axios.isAxiosError(error) &&
      error.response?.status === 410 &&
      staleWorkspaceHandler !== undefined
    ) {
      const sentId = error.config?.headers?.["X-Workspace-Id"];
      if (typeof sentId === "string" && sentId.length > 0) {
        staleWorkspaceHandler(sentId);
      }
    }
    return Promise.reject(error);
  },
);
